import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import './GestaoConteudo.css';
import {
    FaPlus, FaPen, FaTrash, FaToggleOn, FaToggleOff,
    FaUsers, FaUserTag, FaGlobeAmericas, FaImage, FaBullhorn,
    FaImages, FaHistory, FaSync, FaSearch, FaTimes,
    FaRedo, FaChartBar, FaClock, FaCalendarAlt, FaCopy,
    FaCheckCircle, FaExclamationTriangle, FaExclamationCircle, FaInfoCircle,
    FaBuilding
} from 'react-icons/fa';

// ─────────────────────────────────────────────────────────────────────────────
// UTILS
// ─────────────────────────────────────────────────────────────────────────────

const parseJSONField = (field) => {
    if (!field) return [];
    if (Array.isArray(field)) return field;
    try {
        const p = JSON.parse(field);
        return Array.isArray(p) ? p : [];
    } catch {
        return [];
    }
};

// [FASE 3] Autenticação é via cookie HttpOnly (o axios `api` já envia com
// withCredentials). NÃO injetamos mais Authorization manual — fazer isso com um
// token inexistente no localStorage enviaria "Bearer null" e quebraria o login
// header-first do backend. Por isso authHeaders() foi removido.

/**
 * Normaliza o perfil do usuário (canônico), espelhando normalizeRole do backend.
 * O perfil vem por prop (do /me), não mais de um JWT decodificado no cliente.
 */
const normalizeRole = (raw) =>
    String(raw || '')
        .toLowerCase()
        .normalize('NFD')
        .replace(/\p{Diacritic}/gu, '')
        .replace(/[^\w]+/g, '_')
        .trim();

const getInitialFormState = () => ({
    titulo: '',
    mensagem: '',
    ativo: true,
    prioridade: 'info',
    data_inicio: '',
    data_fim: '',
    hora_inicio: '',
    hora_fim: '',
    recorrencia: 'nenhuma',
    intervalo_horas: '',
});

/**
 * (removido) getCurrentUserPerfil — o perfil não é mais decodificado de um JWT
 * no localStorage (que deixou de existir na Fase 3). Agora vem por prop `user`.
 */

const getStatus = (anuncio) => {
    if (!anuncio.ativo) return 'pausado';
    const hoje = new Date();
    hoje.setHours(0, 0, 0, 0);
    if (anuncio.data_inicio) {
        const ini = new Date(anuncio.data_inicio);
        if (ini > hoje) return 'agendado';
    }
    if (anuncio.data_fim) {
        const fim = new Date(anuncio.data_fim);
        if (fim < hoje) return 'encerrado';
    }
    return 'ativo';
};

const STATUS_META = {
    ativo:     { label: 'Ativo',     cls: 'status-ativo'     },
    agendado:  { label: 'Agendado',  cls: 'status-agendado'  },
    encerrado: { label: 'Encerrado', cls: 'status-encerrado' },
    pausado:   { label: 'Pausado',   cls: 'status-pausado'   },
};

const PRIORIDADE_META = {
    info:    { label: 'Info',    cls: 'prio-info',    icon: <FaInfoCircle /> },
    aviso:   { label: 'Aviso',   cls: 'prio-aviso',   icon: <FaExclamationTriangle /> },
    urgente: { label: 'Urgente', cls: 'prio-urgente', icon: <FaExclamationCircle /> },
    critico: { label: 'Crítico', cls: 'prio-critico', icon: <FaExclamationCircle /> },
};

const RECORRENCIA_META = {
    nenhuma:   'Sem recorrência',
    diaria:    'Diária',
    semanal:   'Semanal',
    por_horas: 'Por horas',
};

const PERFIS_DISPONIVEIS = ['admin', 'admin_geral', 'financeiro', 'operacional', 'motorista'];

// ─────────────────────────────────────────────────────────────────────────────
// MODAL DE ANALYTICS
// ─────────────────────────────────────────────────────────────────────────────

function AnalyticsModal({ anuncioId, onClose }) {
    const [data, setData]       = useState(null);
    const [loading, setLoading] = useState(true);
    const [tab, setTab]         = useState('leram');

    useEffect(() => {
        let mounted = true;
        (async () => {
            try {
                const { data: d } = await api.get(
                    `/api/conteudo/anuncios/${anuncioId}/stats`
                );
                if (mounted) setData(d);
            } catch {
                toast.error('Erro ao carregar estatísticas.');
                onClose();
            } finally {
                if (mounted) setLoading(false);
            }
        })();
        return () => { mounted = false; };
    }, [anuncioId, onClose]);

    const taxa      = data?.taxa_leitura ?? 0;
    const circleLen = 2 * Math.PI * 36;
    const dashOffset = circleLen * (1 - taxa / 100);
    const rows      = tab === 'leram' ? data?.leituras || [] : data?.nao_leram || [];

    return (
        <div className="gc-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="gc-modal-box analytics-modal">
                <button className="gc-btn-close-modal" onClick={onClose}>&times;</button>
                <h3><FaChartBar style={{ marginRight: 10 }} />Analytics — {data?.anuncio_titulo || 'Anúncio'}</h3>

                {loading ? (
                    <div className="gc-modal-loading"><p>Carregando estatísticas…</p></div>
                ) : (
                    <>
                        <div className="analytics-kpi-row">
                            <div className="analytics-donut-wrap">
                                <svg viewBox="0 0 80 80" className="analytics-donut">
                                    <circle cx="40" cy="40" r="36" className="donut-track" />
                                    <circle
                                        cx="40" cy="40" r="36"
                                        className="donut-fill"
                                        strokeDasharray={circleLen}
                                        strokeDashoffset={dashOffset}
                                        style={{ transform: 'rotate(-90deg)', transformOrigin: '50% 50%' }}
                                    />
                                    <text x="40" y="36" className="donut-pct">{taxa}%</text>
                                    <text x="40" y="50" className="donut-label">lidos</text>
                                </svg>
                            </div>
                            <div className="analytics-kpis">
                                <div className="kpi-card kpi-total">
                                    <span className="kpi-num">{data?.total_usuarios ?? 0}</span>
                                    <span className="kpi-desc">Usuários no público-alvo</span>
                                </div>
                                <div className="kpi-card kpi-leram">
                                    <FaCheckCircle />
                                    <span className="kpi-num">{data?.total_leram ?? 0}</span>
                                    <span className="kpi-desc">Leram</span>
                                </div>
                                <div className="kpi-card kpi-pendente">
                                    <FaClock />
                                    <span className="kpi-num">{data?.total_nao_leram ?? 0}</span>
                                    <span className="kpi-desc">Pendentes</span>
                                </div>
                            </div>
                        </div>

                        <div className="analytics-tabs">
                            <button className={tab === 'leram' ? 'active' : ''} onClick={() => setTab('leram')}>
                                Quem leu ({data?.total_leram ?? 0})
                            </button>
                            <button className={tab === 'nao_leram' ? 'active' : ''} onClick={() => setTab('nao_leram')}>
                                Pendentes ({data?.total_nao_leram ?? 0})
                            </button>
                        </div>

                        <div className="analytics-table-wrap">
                            <table className="gc-table">
                                <thead>
                                    <tr>
                                        <th>Usuário</th>
                                        <th>Perfil</th>
                                        {tab === 'leram' && <th>Data de leitura</th>}
                                    </tr>
                                </thead>
                                <tbody>
                                    {rows.length === 0 ? (
                                        <tr>
                                            <td colSpan={tab === 'leram' ? 3 : 2} className="gc-empty-msg">
                                                Nenhum registro.
                                            </td>
                                        </tr>
                                    ) : rows.map(u => (
                                        <tr key={u.id}>
                                            <td><strong>{u.nome}</strong></td>
                                            <td><span className="perfil-badge">{u.perfil}</span></td>
                                            {tab === 'leram' && (
                                                <td>
                                                    {u.data_leitura
                                                        ? new Date(u.data_leitura).toLocaleString('pt-BR')
                                                        : '-'}
                                                </td>
                                            )}
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </>
                )}
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// MODAL DE FORMULÁRIO
//
// targetTenantId: quando admin_geral, indica qual tenant pertence o anúncio.
//   - Usado para carregar os usuários corretos na aba Destinatários
//   - Enviado como target_tenant_id no FormData ao salvar
// ─────────────────────────────────────────────────────────────────────────────

function AnuncioFormModal({ anuncio, onClose, onSave, targetTenantId, isAdminGeral = false }) {
    const isEditing = Boolean(anuncio?.id);

    const [formTab, setFormTab]               = useState('conteudo');
    const [form, setForm]                     = useState(getInitialFormState);
    const [imagemFile, setImagemFile]         = useState(null);
    const [imagemPreview, setImagemPreview]   = useState(null);
    const [removeImagem, setRemoveImagem]     = useState(false);
    const [destTipo, setDestTipo]             = useState('todos');
    const [selPerfis, setSelPerfis]           = useState(new Set());
    const [selUsuarios, setSelUsuarios]       = useState(new Set());
    const [usuarios, setUsuarios]             = useState([]);
    const [loadingUsers, setLoadingUsers]     = useState(false);
    const [userSearch, setUserSearch]         = useState('');

    // Para admin_geral: tenant selector dentro do modal (aba Destinatários > Usuários)
    // isAdminGeral agora vem por prop (derivado de user.perfil no topo).
    const [tenants, setTenants]               = useState([]);
    const [destTenantId, setDestTenantId]     = useState(null);

    const fileInputRef = useRef(null);
    const objectUrlRef = useRef(null);

    // ── Carrega lista de tenants para admin_geral (seletor dentro de Destinatários) ──
    useEffect(() => {
        if (!isAdminGeral) return;
        api.get('/api/slideshow/manage/tenants')
            .then(({ data }) => {
                const list = Array.isArray(data) ? data : [];
                setTenants(list);
                // Pré-seleciona o tenant do anúncio (edit) ou o targetTenantId (new)
                const initial = anuncio?.tenant_id || targetTenantId || (list[0]?.id ?? null);
                setDestTenantId(Number(initial) || null);
            })
            .catch(() => {});
    }, [isAdminGeral, anuncio, targetTenantId]);

    // ── Carrega usuários sempre que destTenantId mudar ──
    useEffect(() => {
        // Para admin comum: carrega do próprio tenant (sem filtro extra)
        // Para admin_geral: carrega do tenant selecionado no seletor
        const tenantFilter = isAdminGeral ? destTenantId : null;

        // Admin comum sem destTenantId: carrega normalmente (backend usa req.tenantId)
        // Admin_geral sem destTenantId ainda não selecionou: aguarda
        if (isAdminGeral && !tenantFilter) return;

        let mounted = true;
        setLoadingUsers(true);
        setUsuarios([]);

        const qs = tenantFilter ? `?cliente_id=${tenantFilter}&limit=500` : '?limit=500';
        (async () => {
            try {
                const { data } = await api.get(`/api/usuarios${qs}`);
                // Backend retorna { items, total, page, limit } — corrigido aqui
                if (mounted) setUsuarios(Array.isArray(data) ? data : (data.items || data.data || []));
            } catch {
                console.error('Falha ao carregar usuários.');
            } finally {
                if (mounted) setLoadingUsers(false);
            }
        })();
        return () => { mounted = false; };
    }, [isAdminGeral, destTenantId]);

    // ── Preenche o form ao abrir ──
    useEffect(() => {
        setFormTab('conteudo');
        setImagemFile(null);
        setRemoveImagem(false);
        setUserSearch('');

        if (fileInputRef.current) fileInputRef.current.value = '';
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
        }

        if (!anuncio) {
            setForm(getInitialFormState());
            setImagemPreview(null);
            setDestTipo('todos');
            setSelPerfis(new Set());
            setSelUsuarios(new Set());
            return;
        }

        setForm({
            titulo:          anuncio.titulo || '',
            mensagem:        anuncio.mensagem || '',
            ativo:           anuncio.ativo === undefined ? true : !!anuncio.ativo,
            prioridade:      anuncio.prioridade || 'info',
            data_inicio:     anuncio.data_inicio
                ? new Date(anuncio.data_inicio).toISOString().split('T')[0] : '',
            data_fim:        anuncio.data_fim
                ? new Date(anuncio.data_fim).toISOString().split('T')[0] : '',
            hora_inicio:     anuncio.hora_inicio || '',
            hora_fim:        anuncio.hora_fim || '',
            recorrencia:     anuncio.recorrencia || 'nenhuma',
            intervalo_horas: anuncio.intervalo_horas != null ? String(anuncio.intervalo_horas) : '',
        });

        setImagemPreview(isEditing ? (anuncio.imagem_url || null) : null);

        const perfis     = parseJSONField(anuncio.dest_perfis);
        const usuarioIds = parseJSONField(anuncio.dest_usuarios_ids);

        if (perfis.length) {
            setDestTipo('perfis');
            setSelPerfis(new Set(perfis));
            setSelUsuarios(new Set());
        } else if (usuarioIds.length) {
            setDestTipo('usuarios');
            setSelUsuarios(new Set(usuarioIds.map(Number)));
            setSelPerfis(new Set());
        } else {
            setDestTipo('todos');
            setSelPerfis(new Set());
            setSelUsuarios(new Set());
        }
    }, [anuncio, isEditing]);

    useEffect(() => {
        return () => {
            if (objectUrlRef.current) {
                URL.revokeObjectURL(objectUrlRef.current);
                objectUrlRef.current = null;
            }
        };
    }, []);

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm(prev => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    };

    const handleImageChange = (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
        }
        const previewUrl = URL.createObjectURL(file);
        objectUrlRef.current = previewUrl;
        setImagemFile(file);
        setImagemPreview(previewUrl);
        setRemoveImagem(false);
    };

    const handleRemoveImage = () => {
        if (objectUrlRef.current) {
            URL.revokeObjectURL(objectUrlRef.current);
            objectUrlRef.current = null;
        }
        setImagemPreview(null);
        setImagemFile(null);
        setRemoveImagem(true);
        if (fileInputRef.current) fileInputRef.current.value = '';
    };

    const handlePreviewError = (e) => {
        e.currentTarget.style.display = 'none';
        if (isEditing && anuncio?.imagem_url) setRemoveImagem(true);
        setImagemPreview(null);
        toast.warn('Não foi possível carregar a imagem atual. Selecione uma nova imagem para substituir.');
    };

    const togglePerfil = (perfil) => {
        setSelPerfis(prev => {
            const next = new Set(prev);
            next.has(perfil) ? next.delete(perfil) : next.add(perfil);
            return next;
        });
    };

    const toggleUsuario = (id) => {
        setSelUsuarios(prev => {
            const next = new Set(prev);
            const num  = Number(id);
            next.has(num) ? next.delete(num) : next.add(num);
            return next;
        });
    };

    const handleSubmit = (e) => {
        e.preventDefault();

        if (destTipo === 'perfis' && selPerfis.size === 0) {
            toast.warn('Selecione ao menos um perfil ou altere o destino para todos os usuários.');
            setFormTab('destinatarios');
            return;
        }
        if (destTipo === 'usuarios' && selUsuarios.size === 0) {
            toast.warn('Selecione ao menos um usuário ou altere o destino para todos os usuários.');
            setFormTab('destinatarios');
            return;
        }
        if (form.recorrencia === 'por_horas' && !form.intervalo_horas) {
            toast.warn('Informe o intervalo em horas para a recorrência personalizada.');
            setFormTab('agendamento');
            return;
        }
        // Admin geral precisa ter tenant definido
        if (isAdminGeral && !targetTenantId && !anuncio?.id) {
            toast.warn('Selecione um tenant antes de criar o anúncio.');
            return;
        }

        const fd = new FormData();
        if (isEditing) fd.append('id', anuncio.id);
        Object.entries(form).forEach(([key, value]) => fd.append(key, value));
        if (imagemFile) fd.append('imagem', imagemFile);
        fd.append('remove_imagem', removeImagem ? 'true' : 'false');

        // Envia o tenant-alvo para o backend (admin_geral)
        const resolvedTenant = targetTenantId || anuncio?.tenant_id;
        if (isAdminGeral && resolvedTenant) {
            fd.append('target_tenant_id', String(resolvedTenant));
        }

        if (destTipo === 'perfis') {
            fd.append('dest_perfis', JSON.stringify([...selPerfis]));
            fd.append('dest_usuarios_ids', JSON.stringify([]));
        } else if (destTipo === 'usuarios') {
            fd.append('dest_perfis', JSON.stringify([]));
            fd.append('dest_usuarios_ids', JSON.stringify([...selUsuarios]));
        } else {
            fd.append('dest_perfis', JSON.stringify([]));
            fd.append('dest_usuarios_ids', JSON.stringify([]));
        }

        onSave(fd, isEditing);
    };

    const usuariosFiltrados = useMemo(() => {
        const term = userSearch.trim().toLowerCase();
        if (!term) return usuarios;
        return usuarios.filter(u =>
            (u.nome || '').toLowerCase().includes(term) ||
            (u.perfil || '').toLowerCase().includes(term)
        );
    }, [usuarios, userSearch]);

    const FORM_TABS = [
        { key: 'conteudo',      label: '✏️ Conteúdo'      },
        { key: 'agendamento',   label: '📅 Agendamento'   },
        { key: 'destinatarios', label: '👥 Destinatários' },
    ];

    // Nome do tenant selecionado para exibir no header do modal
    const tenantSelecionadoNome = useMemo(() => {
        if (!isAdminGeral) return null;
        const tid = targetTenantId || anuncio?.tenant_id;
        if (!tid) return null;
        const t = tenants.find(t => t.id === Number(tid));
        return t?.nome_empresa || null;
    }, [isAdminGeral, tenants, targetTenantId, anuncio]);

    return (
        <div className="gc-modal-overlay" onClick={e => e.target === e.currentTarget && onClose()}>
            <div className="gc-modal-box form-modal">
                <button className="gc-btn-close-modal" onClick={onClose}>&times;</button>
                <h3>{isEditing ? 'Editar Anúncio' : 'Criar Novo Anúncio'}</h3>

                {/* Badge de tenant para admin_geral */}
                {isAdminGeral && tenantSelecionadoNome && (
                    <div className="gc-modal-tenant-badge">
                        <FaBuilding />
                        <span>{tenantSelecionadoNome}</span>
                    </div>
                )}

                <div className="form-modal-tabs">
                    {FORM_TABS.map(t => (
                        <button
                            key={t.key}
                            className={formTab === t.key ? 'active' : ''}
                            onClick={() => setFormTab(t.key)}
                            type="button"
                        >
                            {t.label}
                        </button>
                    ))}
                </div>

                <form onSubmit={handleSubmit} className="gc-form">
                    {/* ── Tab: CONTEÚDO ── */}
                    {formTab === 'conteudo' && (
                        <div className="form-tab-content">
                            <div className="gc-form-body">
                                <div className="gc-col-main">
                                    <div className="gc-form-group">
                                        <label>Título *</label>
                                        <input
                                            type="text" name="titulo"
                                            value={form.titulo} onChange={handleChange} required
                                        />
                                    </div>
                                    <div className="gc-form-group">
                                        <label>Mensagem *</label>
                                        <textarea
                                            name="mensagem" value={form.mensagem}
                                            onChange={handleChange} required rows="6"
                                        />
                                    </div>
                                    <div className="gc-form-row">
                                        <div className="gc-form-group">
                                            <label>Prioridade</label>
                                            <select
                                                name="prioridade" value={form.prioridade}
                                                onChange={handleChange} className="gc-select"
                                            >
                                                <option value="info">ℹ️ Info</option>
                                                <option value="aviso">⚠️ Aviso</option>
                                                <option value="urgente">🔴 Urgente</option>
                                                <option value="critico">🚨 Crítico</option>
                                            </select>
                                        </div>
                                        <div className="gc-form-group gc-form-inline-group">
                                            <label>Anúncio ativo?</label>
                                            <label className="gc-switch">
                                                <input
                                                    type="checkbox" name="ativo"
                                                    checked={form.ativo} onChange={handleChange}
                                                />
                                                <span className="gc-slider"></span>
                                            </label>
                                        </div>
                                    </div>
                                </div>

                                <div className="gc-col-side">
                                    <label>Imagem (Opcional)</label>
                                    <div
                                        className={`gc-img-upload-area ${imagemPreview ? 'has-image' : ''}`}
                                        onClick={() => fileInputRef.current?.click()}
                                        role="button" tabIndex={0}
                                        onKeyDown={e => {
                                            if (e.key === 'Enter' || e.key === ' ')
                                                fileInputRef.current?.click();
                                        }}
                                    >
                                        {imagemPreview ? (
                                            <img src={imagemPreview} alt="Preview" onError={handlePreviewError} />
                                        ) : (
                                            <span>
                                                <FaImage /><br />
                                                Clique para selecionar
                                                <small style={{ display: 'block', marginTop: 6 }}>
                                                    PNG, JPG ou WEBP
                                                </small>
                                            </span>
                                        )}
                                    </div>
                                    <input
                                        id="gc-img-input" ref={fileInputRef}
                                        type="file" accept="image/png,image/jpeg,image/webp"
                                        onChange={handleImageChange} style={{ display: 'none' }}
                                    />
                                    {imagemPreview && (
                                        <button
                                            type="button" className="gc-btn-remove-img"
                                            onClick={handleRemoveImage}
                                        >
                                            <FaTimes /> Remover imagem
                                        </button>
                                    )}
                                    {!imagemPreview && removeImagem && isEditing && (
                                        <p className="dest-hint" style={{ marginTop: 8 }}>
                                            A imagem atual será removida ao salvar.
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Tab: AGENDAMENTO ── */}
                    {formTab === 'agendamento' && (
                        <div className="form-tab-content">
                            <div className="sched-grid">
                                <div className="sched-section">
                                    <h4><FaCalendarAlt /> Período de exibição</h4>
                                    <div className="gc-form-row">
                                        <div className="gc-form-group">
                                            <label>Data início</label>
                                            <input
                                                type="date" name="data_inicio"
                                                value={form.data_inicio} onChange={handleChange}
                                            />
                                        </div>
                                        <div className="gc-form-group">
                                            <label>Data fim</label>
                                            <input
                                                type="date" name="data_fim"
                                                value={form.data_fim} onChange={handleChange}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="sched-section">
                                    <h4><FaClock /> Horário de exibição</h4>
                                    <p className="sched-hint">
                                        Defina um intervalo de horas para o anúncio aparecer apenas nesse período do dia.
                                    </p>
                                    <div className="gc-form-row">
                                        <div className="gc-form-group">
                                            <label>A partir de</label>
                                            <input
                                                type="time" name="hora_inicio"
                                                value={form.hora_inicio} onChange={handleChange}
                                            />
                                        </div>
                                        <div className="gc-form-group">
                                            <label>Até</label>
                                            <input
                                                type="time" name="hora_fim"
                                                value={form.hora_fim} onChange={handleChange}
                                            />
                                        </div>
                                    </div>
                                </div>

                                <div className="sched-section">
                                    <h4><FaSync /> Recorrência</h4>
                                    <p className="sched-hint">
                                        Com recorrência ativa, o anúncio reaparece periodicamente mesmo para quem já viu.
                                    </p>
                                    <div className="gc-form-group">
                                        <label>Tipo de recorrência</label>
                                        <select
                                            name="recorrencia" value={form.recorrencia}
                                            onChange={handleChange} className="gc-select"
                                        >
                                            <option value="nenhuma">Sem recorrência (exibe 1 vez)</option>
                                            <option value="diaria">Diária (reaparece todo dia)</option>
                                            <option value="semanal">Semanal (reaparece a cada 7 dias)</option>
                                            <option value="por_horas">Por horas (intervalo customizável)</option>
                                        </select>
                                    </div>
                                    {form.recorrencia === 'por_horas' && (
                                        <div className="gc-form-group">
                                            <label>Intervalo (em horas) *</label>
                                            <input
                                                type="number" name="intervalo_horas"
                                                value={form.intervalo_horas} onChange={handleChange}
                                                min="1" max="8760" required
                                                placeholder="Ex: 4 (reaparece a cada 4 horas)"
                                            />
                                        </div>
                                    )}
                                    {form.recorrencia !== 'nenhuma' && (
                                        <div className="sched-recurrence-preview">
                                            <FaInfoCircle />
                                            {form.recorrencia === 'diaria' &&
                                                'O anúncio reaparece para o usuário uma vez por dia.'}
                                            {form.recorrencia === 'semanal' &&
                                                'O anúncio reaparece uma vez por semana.'}
                                            {form.recorrencia === 'por_horas' && form.intervalo_horas &&
                                                `O anúncio reaparece a cada ${form.intervalo_horas}h após a última visualização.`}
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* ── Tab: DESTINATÁRIOS ── */}
                    {formTab === 'destinatarios' && (
                        <div className="form-tab-content">
                            <div className="dest-section">
                                <h4>Quem deve ver este anúncio?</h4>
                                <div className="dest-radio-group">
                                    {[
                                        { val: 'todos',    icon: <FaGlobeAmericas />, label: 'Todos os usuários'    },
                                        { val: 'perfis',   icon: <FaUserTag />,       label: 'Por perfil'           },
                                        { val: 'usuarios', icon: <FaUsers />,         label: 'Usuários específicos' },
                                    ].map(({ val, icon, label }) => (
                                        <label
                                            key={val}
                                            className={`dest-radio-card ${destTipo === val ? 'selected' : ''}`}
                                        >
                                            <input
                                                type="radio" value={val}
                                                checked={destTipo === val}
                                                onChange={e => setDestTipo(e.target.value)}
                                            />
                                            <span className="dest-card-icon">{icon}</span>
                                            <span>{label}</span>
                                        </label>
                                    ))}
                                </div>

                                {destTipo === 'perfis' && (
                                    <div className="dest-list-area">
                                        <p className="dest-hint">Selecione um ou mais perfis:</p>
                                        <div className="gc-checkbox-list scroll">
                                            {PERFIS_DISPONIVEIS.map(p => (
                                                <label
                                                    key={p}
                                                    className={`gc-check-item ${selPerfis.has(p) ? 'checked' : ''}`}
                                                >
                                                    <input
                                                        type="checkbox" checked={selPerfis.has(p)}
                                                        onChange={() => togglePerfil(p)}
                                                    />
                                                    <span className="perfil-badge">{p.replace('_', ' ')}</span>
                                                </label>
                                            ))}
                                        </div>
                                        {selPerfis.size > 0 && (
                                            <p className="dest-summary">
                                                {selPerfis.size} perfil(is) selecionado(s): {[...selPerfis].join(', ')}
                                            </p>
                                        )}
                                    </div>
                                )}

                                {destTipo === 'usuarios' && (
                                    <div className="dest-list-area">
                                        {/* Seletor de tenant dentro do modal — apenas para admin_geral */}
                                        {isAdminGeral && (
                                            <div className="gc-dest-tenant-selector">
                                                <FaBuilding className="gc-dest-tenant-icon" />
                                                <label>Carregar usuários de:</label>
                                                <select
                                                    value={destTenantId || ''}
                                                    onChange={e => {
                                                        setDestTenantId(Number(e.target.value));
                                                        setSelUsuarios(new Set()); // Limpa seleção ao trocar tenant
                                                    }}
                                                    className="gc-dest-tenant-select"
                                                >
                                                    {tenants.map(t => (
                                                        <option key={t.id} value={t.id}>{t.nome_empresa}</option>
                                                    ))}
                                                </select>
                                            </div>
                                        )}

                                        <div className="gc-search-box compact" style={{ marginTop: 8 }}>
                                            <FaSearch className="search-icon" />
                                            <input
                                                type="text" placeholder="Filtrar usuários…"
                                                value={userSearch} onChange={e => setUserSearch(e.target.value)}
                                            />
                                            {userSearch && (
                                                <button
                                                    type="button" className="gc-btn-clear"
                                                    onClick={() => setUserSearch('')}
                                                >
                                                    <FaTimes />
                                                </button>
                                            )}
                                        </div>
                                        {loadingUsers ? (
                                            <p className="dest-hint" style={{ marginTop: 8 }}>
                                                <FaSync className="spin" style={{ marginRight: 6 }} />
                                                Carregando usuários…
                                            </p>
                                        ) : (
                                            <div className="gc-checkbox-list scroll tall">
                                                {usuariosFiltrados.length === 0 ? (
                                                    <p className="dest-hint" style={{ padding: 16 }}>
                                                        {isAdminGeral && !destTenantId
                                                            ? 'Selecione um tenant acima para ver os usuários.'
                                                            : 'Nenhum usuário encontrado.'}
                                                    </p>
                                                ) : usuariosFiltrados.map(u => (
                                                    <label
                                                        key={u.id}
                                                        className={`gc-check-item ${selUsuarios.has(Number(u.id)) ? 'checked' : ''}`}
                                                    >
                                                        <input
                                                            type="checkbox"
                                                            checked={selUsuarios.has(Number(u.id))}
                                                            onChange={() => toggleUsuario(u.id)}
                                                        />
                                                        <span className="user-check-name">{u.nome}</span>
                                                        <span className="perfil-badge sm">{u.perfil}</span>
                                                    </label>
                                                ))}
                                            </div>
                                        )}
                                        {selUsuarios.size > 0 && (
                                            <p className="dest-summary">
                                                {selUsuarios.size} usuário(s) selecionado(s)
                                            </p>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    )}

                    <div className="gc-form-footer">
                        <button type="button" className="gc-btn-secondary" onClick={onClose}>
                            Cancelar
                        </button>
                        <button type="submit" className="gc-btn-submit">
                            {isEditing ? 'Salvar alterações' : 'Criar anúncio'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}

// ─────────────────────────────────────────────────────────────────────────────
// RENDERIZAR DESTINATÁRIOS NA LISTAGEM
// ─────────────────────────────────────────────────────────────────────────────

const renderDestinatarios = (anuncio) => {
    const perfis   = parseJSONField(anuncio.dest_perfis);
    const usuarios = parseJSONField(anuncio.dest_usuarios_ids);

    if (perfis.length) return (
        <><FaUserTag /><span>{perfis.join(', ')}</span></>
    );
    if (usuarios.length) return (
        <><FaUsers /><span>{usuarios.length} usuário(s)</span></>
    );
    return (
        <><FaGlobeAmericas /><span>Todos</span></>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// SLIDESHOW MANAGER (sem alterações — mantido idêntico)
// ─────────────────────────────────────────────────────────────────────────────

const SlideshowManager = ({ isAdminGeral = false }) => {
    const MAX = 20;

    const [images, setImages]     = useState([]);
    const [tenants, setTenants]     = useState([]);
    const [allImages, setAllImages] = useState([]);
    const [loadingTenants, setLoadingTenants] = useState(false);
    const [loading, setLoading]                   = useState(true);
    const [uploadTargetTenantId, setUploadTarget] = useState(null);

    const fileRef = useRef(null);

    const imagesByTenant = useMemo(() => {
        if (!isAdminGeral) return {};
        return allImages.reduce((acc, img) => {
            const tid = String(img.tenant_id);
            if (!acc[tid]) acc[tid] = [];
            acc[tid].push(img);
            return acc;
        }, {});
    }, [allImages, isAdminGeral]);

    useEffect(() => {
        if (!isAdminGeral) return;
        setLoadingTenants(true);
        api.get('/api/slideshow/manage/tenants')
            .then(({ data }) => setTenants(Array.isArray(data) ? data : []))
            .catch(() => toast.error('Falha ao carregar lista de tenants.'))
            .finally(() => setLoadingTenants(false));
    }, [isAdminGeral]);

    const load = useCallback(async () => {
        try {
            setLoading(true);
            const { data } = await api.get('/api/slideshow/manage/images');
            const imgs = Array.isArray(data) ? data : [];
            isAdminGeral ? setAllImages(imgs) : setImages(imgs);
        } catch {
            toast.error('Falha ao carregar slideshow.');
            isAdminGeral ? setAllImages([]) : setImages([]);
        } finally {
            setLoading(false);
        }
    }, [isAdminGeral]);

    useEffect(() => { load(); }, [load]);

    const triggerUpload = useCallback((tenantId = null) => {
        setUploadTarget(tenantId);
        if (fileRef.current) fileRef.current.value = '';
        fileRef.current?.click();
    }, []);

    const upload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        if (isAdminGeral) {
            const count = imagesByTenant[String(uploadTargetTenantId)]?.length ?? 0;
            if (count >= MAX) {
                toast.warn(`Limite de ${MAX} imagens já atingido para este tenant.`);
                e.target.value = '';
                return;
            }
        } else {
            if (images.length >= MAX) {
                toast.warn(`Limite de ${MAX} imagens atingido.`);
                e.target.value = '';
                return;
            }
        }

        const fd = new FormData();
        fd.append('image', file);

        if (isAdminGeral) {
            if (!uploadTargetTenantId) {
                toast.error('Nenhum tenant selecionado para o upload.');
                e.target.value = '';
                return;
            }
            fd.append('target_tenant_id', String(uploadTargetTenantId));
        }

        try {
            await api.post('/api/slideshow/manage/images', fd);
            toast.success('Imagem adicionada!');
            load();
        } catch (err) {
            toast.error(err.response?.data?.error || 'Erro ao enviar imagem.');
        } finally {
            e.target.value = '';
            setUploadTarget(null);
        }
    };

    const del = async (id) => {
        if (!window.confirm('Excluir esta imagem?')) return;
        try {
            await api.delete(`/api/slideshow/manage/images/${id}`);
            toast.success('Imagem excluída!');
            load();
        } catch {
            toast.error('Erro ao excluir imagem.');
        }
    };

    const sharedFileInput = (
        <input
            type="file" ref={fileRef} onChange={upload}
            accept="image/png,image/jpeg,image/webp"
            style={{ display: 'none' }}
        />
    );

    if (isAdminGeral) {
        return (
            <>
                <div className="gc-section-header">
                    <h3>Banners — Todos os Tenants</h3>
                    <span className="gc-section-hint">
                        Gerencie os banners de cada empresa separadamente.
                    </span>
                </div>

                {sharedFileInput}

                {(loading || loadingTenants) ? (
                    <div className="gc-loading-state"><FaSync className="spin" /> Carregando…</div>
                ) : tenants.length === 0 ? (
                    <div className="gc-empty-state">
                        <FaImages /><p>Nenhum tenant encontrado.</p>
                    </div>
                ) : (
                    <div className="gc-tenants-slideshow-list">
                        {tenants.map(tenant => {
                            const imgs    = imagesByTenant[String(tenant.id)] ?? [];
                            const atLimit = imgs.length >= MAX;

                            return (
                                <div key={tenant.id} className="gc-tenant-slideshow-section">
                                    <div className="gc-tenant-section-header">
                                        <div className="gc-tenant-title">
                                            <span className="gc-tenant-name">{tenant.nome_empresa}</span>
                                            <span
                                                className={`gc-tenant-count ${atLimit ? 'at-limit' : ''}`}
                                                title={atLimit ? 'Limite atingido' : `${MAX - imgs.length} vagas disponíveis`}
                                            >
                                                {imgs.length}/{MAX}
                                            </span>
                                        </div>
                                        <button
                                            className="gc-btn-add sm"
                                            onClick={() => triggerUpload(tenant.id)}
                                            disabled={atLimit}
                                            title={atLimit ? `Limite de ${MAX} imagens atingido` : `Adicionar imagem para ${tenant.nome_empresa}`}
                                        >
                                            <FaImage /> Adicionar
                                        </button>
                                    </div>

                                    {imgs.length === 0 ? (
                                        <p className="gc-tenant-empty-msg">
                                            Nenhuma imagem cadastrada para este tenant.
                                        </p>
                                    ) : (
                                        <div className="gc-grid-slideshow compact">
                                            {imgs.map(img => (
                                                <div key={img.id} className="gc-slide-card">
                                                    <img
                                                        src={img.image_url} alt={`Banner ${img.id}`}
                                                        onError={e => { e.currentTarget.style.opacity = '0.3'; }}
                                                    />
                                                    <div className="gc-slide-overlay">
                                                        <span>Por: {img.criado_por_nome || '—'}</span>
                                                        <button
                                                            className="gc-btn-overlay-delete"
                                                            onClick={() => del(img.id)}
                                                            title="Excluir imagem"
                                                        >
                                                            <FaTrash />
                                                        </button>
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                )}
            </>
        );
    }

    return (
        <>
            <div className="gc-section-header">
                <h3>
                    Gerenciar Banners{' '}
                    <span className="header-counter">({images.length}/{MAX})</span>
                </h3>
                {sharedFileInput}
                <button
                    onClick={() => triggerUpload(null)}
                    className="gc-btn-add"
                    disabled={images.length >= MAX}
                    title={images.length >= MAX ? `Limite de ${MAX} imagens atingido` : 'Adicionar imagem'}
                >
                    <FaImage /> Nova Imagem
                </button>
            </div>

            {loading ? (
                <div className="gc-loading-state"><FaSync className="spin" /> Carregando…</div>
            ) : images.length === 0 ? (
                <div className="gc-empty-state">
                    <FaImages /><p>Nenhuma imagem no slideshow.</p>
                </div>
            ) : (
                <div className="gc-grid-slideshow">
                    {images.map(img => (
                        <div key={img.id} className="gc-slide-card">
                            <img src={img.image_url} alt={`Slide ${img.id}`} />
                            <div className="gc-slide-overlay">
                                <span>Por: {img.criado_por_nome}</span>
                                <button className="gc-btn-overlay-delete" onClick={() => del(img.id)}>
                                    <FaTrash />
                                </button>
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// ANÚNCIOS MANAGER
//
// admin_geral: exibe selector de tenant no topo para filtrar anúncios por empresa.
//   O tenant selecionado é repassado ao modal via onOpenModal(anuncio, tenantId).
// ─────────────────────────────────────────────────────────────────────────────

const AnunciosManager = ({ onOpenModal, refreshKey, isAdminGeral = false }) => {

    const [anuncios, setAnuncios]             = useState([]);
    const [loading, setLoading]               = useState(true);
    const [statsId, setStatsId]               = useState(null);
    const [filterStatus, setFilterStatus]     = useState('todos');

    // Para admin_geral — seletor de tenant no topo
    const [tenants, setTenants]               = useState([]);
    const [selectedTenantId, setSelectedTenantId] = useState(null);
    const [loadingTenants, setLoadingTenants] = useState(false);

    // Carrega lista de tenants para admin_geral
    useEffect(() => {
        if (!isAdminGeral) return;
        setLoadingTenants(true);
        api.get('/api/slideshow/manage/tenants')
            .then(({ data }) => {
                const list = Array.isArray(data) ? data : [];
                setTenants(list);
                if (list.length > 0) setSelectedTenantId(list[0].id);
            })
            .catch(() => toast.error('Falha ao carregar lista de empresas.'))
            .finally(() => setLoadingTenants(false));
    }, [isAdminGeral]);

    const load = useCallback(async () => {
        // Admin geral sem tenant selecionado: aguarda
        if (isAdminGeral && !selectedTenantId) {
            setAnuncios([]);
            setLoading(false);
            return;
        }
        try {
            setLoading(true);
            const qs = isAdminGeral ? `?target_tenant_id=${selectedTenantId}` : '';
            const { data } = await api.get(`/api/conteudo/anuncios${qs}`);
            const raw = Array.isArray(data) ? data : (data.data || []);
            setAnuncios(raw.map(a => ({
                ...a,
                dest_perfis:       parseJSONField(a.dest_perfis),
                dest_usuarios_ids: parseJSONField(a.dest_usuarios_ids),
            })));
        } catch {
            console.error('Erro ao buscar anúncios.');
            setAnuncios([]);
        } finally {
            setLoading(false);
        }
    }, [isAdminGeral, selectedTenantId]);

    useEffect(() => { load(); }, [load, refreshKey]);

    const handleDelete = async (id) => {
        if (!window.confirm('Excluir este anúncio?')) return;
        try {
            const qs = isAdminGeral && selectedTenantId ? `?target_tenant_id=${selectedTenantId}` : '';
            await api.delete(`/api/conteudo/anuncios/${id}${qs}`);
            toast.success('Anúncio excluído!');
            load();
        } catch {
            toast.error('Erro ao excluir anúncio.');
        }
    };

    const handleReenviar = async (id, titulo) => {
        if (!window.confirm(`Reenviar "${titulo}"?\n\nIsso fará o anúncio reaparecer para todos os usuários que já o viram.`))
            return;
        try {
            const qs = isAdminGeral && selectedTenantId ? `?target_tenant_id=${selectedTenantId}` : '';
            const { data } = await api.post(
                `/api/conteudo/anuncios/${id}/reenviar${qs}`, {}
            );
            toast.success(`Reenviado! ${data.leituras_resetadas} leitura(s) resetada(s).`);
            load();
        } catch {
            toast.error('Erro ao reenviar anúncio.');
        }
    };

    const handleDuplicar = (anuncio) => {
        onOpenModal({
            ...anuncio,
            id:                     null,
            titulo:                 `Cópia de ${anuncio.titulo}`,
            imagem_url:             null,
            imagem_drive_file_id:   null,
            imagem_drive_folder_id: null,
        }, selectedTenantId || anuncio.tenant_id);
    };

    const filtered = useMemo(() => {
        if (filterStatus === 'todos') return anuncios;
        return anuncios.filter(a => getStatus(a) === filterStatus);
    }, [anuncios, filterStatus]);

    const FILTERS = ['todos', 'ativo', 'agendado', 'pausado', 'encerrado'];

    // Nome do tenant atualmente selecionado
    const tenantNome = useMemo(() => {
        if (!isAdminGeral || !selectedTenantId) return null;
        return tenants.find(t => t.id === selectedTenantId)?.nome_empresa || null;
    }, [isAdminGeral, selectedTenantId, tenants]);

    return (
        <>
            <div className="gc-section-header">
                <h3>Gerenciar Anúncios</h3>
                <button
                    onClick={() => onOpenModal(null, selectedTenantId)}
                    className="gc-btn-add"
                    disabled={isAdminGeral && !selectedTenantId}
                >
                    <FaPlus /> Novo Anúncio
                </button>
            </div>

            {/* ── Seletor de tenant para admin_geral ── */}
            {isAdminGeral && (
                <div className="gc-anuncio-tenant-bar">
                    <FaBuilding className="gc-anuncio-tenant-icon" />
                    <span className="gc-anuncio-tenant-label">Visualizando empresa:</span>
                    {loadingTenants ? (
                        <span className="gc-anuncio-tenant-loading">
                            <FaSync className="spin" /> Carregando…
                        </span>
                    ) : (
                        <select
                            value={selectedTenantId || ''}
                            onChange={e => setSelectedTenantId(Number(e.target.value))}
                            className="gc-anuncio-tenant-select"
                        >
                            {tenants.map(t => (
                                <option key={t.id} value={t.id}>{t.nome_empresa}</option>
                            ))}
                        </select>
                    )}
                    {tenantNome && (
                        <span className="gc-anuncio-tenant-badge">
                            {anuncios.length} anúncio(s)
                        </span>
                    )}
                </div>
            )}

            <div className="gc-filter-bar">
                {FILTERS.map(f => (
                    <button
                        key={f}
                        className={`gc-filter-pill ${filterStatus === f ? 'active' : ''}`}
                        onClick={() => setFilterStatus(f)}
                    >
                        {f === 'todos' ? 'Todos' : STATUS_META[f]?.label}
                        {f !== 'todos' && (
                            <span className="pill-count">
                                {anuncios.filter(a => getStatus(a) === f).length}
                            </span>
                        )}
                    </button>
                ))}
            </div>

            {loading ? (
                <div className="gc-loading-state"><FaSync className="spin" /> Carregando…</div>
            ) : isAdminGeral && !selectedTenantId ? (
                <div className="gc-empty-state">
                    <FaBuilding /><p>Selecione uma empresa acima para ver os anúncios.</p>
                </div>
            ) : filtered.length === 0 ? (
                <div className="gc-empty-state">
                    <FaBullhorn /><p>Nenhum anúncio encontrado.</p>
                </div>
            ) : (
                <ul className="gc-list-anuncios">
                    {filtered.map(anuncio => {
                        const status  = getStatus(anuncio);
                        const statusM = STATUS_META[status];
                        const prioM   = PRIORIDADE_META[anuncio.prioridade || 'info'];

                        return (
                            <li
                                key={anuncio.id}
                                className={`gc-list-item prio-border-${anuncio.prioridade || 'info'}`}
                            >
                                <div className="gc-item-priority-bar" />
                                <div className="gc-item-info">
                                    <div className="gc-item-title-row">
                                        <strong>{anuncio.titulo}</strong>
                                        <span className={`gc-status-badge ${statusM.cls}`}>{statusM.label}</span>
                                        <span className={`gc-prio-badge ${prioM.cls}`}>
                                            {prioM.icon} {prioM.label}
                                        </span>
                                    </div>
                                    <div className="gc-item-meta">
                                        <span className="gc-tag-info">
                                            {renderDestinatarios(anuncio)}
                                        </span>
                                        {anuncio.recorrencia !== 'nenhuma' && (
                                            <span className="gc-tag-recurrence">
                                                <FaSync /> {RECORRENCIA_META[anuncio.recorrencia]}
                                                {anuncio.recorrencia === 'por_horas' && ` (${anuncio.intervalo_horas}h)`}
                                            </span>
                                        )}
                                        {(anuncio.data_inicio || anuncio.data_fim) && (
                                            <span className="gc-tag-date">
                                                <FaCalendarAlt />
                                                {anuncio.data_inicio &&
                                                    new Date(anuncio.data_inicio).toLocaleDateString('pt-BR')}
                                                {anuncio.data_inicio && anuncio.data_fim && ' → '}
                                                {anuncio.data_fim &&
                                                    new Date(anuncio.data_fim).toLocaleDateString('pt-BR')}
                                            </span>
                                        )}
                                        {(anuncio.hora_inicio || anuncio.hora_fim) && (
                                            <span className="gc-tag-date">
                                                <FaClock />
                                                {anuncio.hora_inicio}
                                                {anuncio.hora_fim && ` → ${anuncio.hora_fim}`}
                                            </span>
                                        )}
                                    </div>
                                    <span className="gc-item-author">
                                        Criado por <strong>{anuncio.criado_por_nome || 'Sistema'}</strong> em{' '}
                                        {anuncio.data_criacao
                                            ? new Date(anuncio.data_criacao).toLocaleDateString('pt-BR')
                                            : '-'}
                                    </span>
                                </div>
                                <div className="gc-item-actions">
                                    {anuncio.ativo
                                        ? <FaToggleOn  className="gc-icon-status active"   title="Ativo"   />
                                        : <FaToggleOff className="gc-icon-status inactive" title="Inativo" />
                                    }
                                    <button title="Ver analytics" className="gc-btn-action analytics"
                                        onClick={() => setStatsId(anuncio.id)}>
                                        <FaChartBar />
                                    </button>
                                    <button title="Reenviar para todos" className="gc-btn-action resend"
                                        onClick={() => handleReenviar(anuncio.id, anuncio.titulo)}>
                                        <FaRedo />
                                    </button>
                                    <button title="Duplicar" className="gc-btn-action duplicate"
                                        onClick={() => handleDuplicar(anuncio)}>
                                        <FaCopy />
                                    </button>
                                    <button title="Editar" className="gc-btn-action edit"
                                        onClick={() => onOpenModal(anuncio, selectedTenantId || anuncio.tenant_id)}>
                                        <FaPen />
                                    </button>
                                    <button title="Excluir" className="gc-btn-action delete"
                                        onClick={() => handleDelete(anuncio.id)}>
                                        <FaTrash />
                                    </button>
                                </div>
                            </li>
                        );
                    })}
                </ul>
            )}

            {statsId && (
                <AnalyticsModal anuncioId={statsId} onClose={() => setStatsId(null)} />
            )}
        </>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// LOGS MANAGER (sem alterações)
// ─────────────────────────────────────────────────────────────────────────────

const LogsManager = () => {
    const [logs, setLogs]         = useState([]);
    const [loading, setLoading]   = useState(true);
    const [page, setPage]         = useState(1);
    const [totalPages, setTotal]  = useState(1);
    const [search, setSearch]     = useState('');
    const LIMIT = 20;

    const load = useCallback(async (p = 1, s = '') => {
        try {
            setLoading(true);
            const { data } = await api.get(
                `/api/conteudo/anuncios/logs?page=${p}&limit=${LIMIT}&search=${encodeURIComponent(s)}`
            );
            setLogs(data?.logs || []);
            setTotal(data?.pagination?.totalPages || 1);
            setPage(data?.pagination?.page || 1);
        } catch {
            toast.error('Erro ao carregar histórico.');
            setLogs([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { load(1, ''); }, [load]);

    return (
        <div className="gc-logs-area">
            <div className="gc-section-header">
                <h3>Histórico de Leitura</h3>
                <div className="gc-header-actions">
                    <div className="gc-search-box">
                        <FaSearch className="search-icon" />
                        <input
                            type="text" placeholder="Buscar usuário ou anúncio…"
                            value={search} onChange={e => setSearch(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && load(1, search)}
                        />
                        {search && (
                            <button className="gc-btn-clear" onClick={() => { setSearch(''); load(1, ''); }}>
                                <FaTimes />
                            </button>
                        )}
                        <button className="gc-btn-go" onClick={() => load(1, search)}>Buscar</button>
                    </div>
                    <button className="gc-btn-refresh" onClick={() => load(page, search)}>
                        <FaSync /> Atualizar
                    </button>
                </div>
            </div>

            {loading ? (
                <div className="gc-loading-state"><FaSync className="spin" /> Carregando…</div>
            ) : (
                <>
                    <div className="gc-table-container">
                        <table className="gc-table">
                            <thead>
                                <tr>
                                    <th>Data/Hora</th>
                                    <th>Usuário</th>
                                    <th>Perfil</th>
                                    <th>Anúncio</th>
                                    <th>Tipo</th>
                                </tr>
                            </thead>
                            <tbody>
                                {logs.length === 0 ? (
                                    <tr>
                                        <td colSpan="5" className="gc-empty-msg">Nenhum registro encontrado.</td>
                                    </tr>
                                ) : logs.map((log, i) => (
                                    <tr key={`${log.usuario_nome}-${log.anuncio_titulo}-${log.data}-${i}`}>
                                        <td>{log.data ? new Date(log.data).toLocaleString('pt-BR') : '-'}</td>
                                        <td><strong>{log.usuario_nome}</strong></td>
                                        <td><span className="perfil-badge sm">{log.usuario_perfil}</span></td>
                                        <td>{log.anuncio_titulo}</td>
                                        <td>
                                            <span className={`gc-tag-type ${log.tipo_anuncio?.toLowerCase()}`}>
                                                {log.tipo_anuncio}
                                            </span>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <div className="gc-pagination">
                        <button disabled={page === 1} onClick={() => load(page - 1, search)}>← Anterior</button>
                        <span>Página <strong>{page}</strong> de <strong>{totalPages}</strong></span>
                        <button disabled={page === totalPages} onClick={() => load(page + 1, search)}>Próxima →</button>
                    </div>
                </>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// COMPONENTE PRINCIPAL
// ─────────────────────────────────────────────────────────────────────────────

export default function GestaoConteudo({ user }) {
    const isAdminGeral = normalizeRole(user?.perfil) === 'admin_geral';
    const [tab, setTab]                     = useState('anuncios');
    const [modalOpen, setModalOpen]         = useState(false);
    const [editing, setEditing]             = useState(null);
    const [anuncioTargetTenant, setAnuncioTargetTenant] = useState(null);
    const [refreshKey, setRefreshKey]       = useState(0);

    // Recebe (anuncio, tenantId) — tenantId é usado pelo modal para carregar
    // usuários do tenant correto e para enviar target_tenant_id ao backend
    const openModal = useCallback((anuncio = null, tenantId = null) => {
        setEditing(anuncio);
        setAnuncioTargetTenant(tenantId);
        setModalOpen(true);
    }, []);

    const closeModal = useCallback(() => {
        setModalOpen(false);
        setEditing(null);
        setAnuncioTargetTenant(null);
    }, []);

    const saveAnuncio = useCallback(async (formData, isEditing) => {
        try {
            if (isEditing) {
                await api.put(
                    `/api/conteudo/anuncios/${formData.get('id')}`,
                    formData
                );
                toast.success('Anúncio atualizado!');
            } else {
                await api.post('/api/conteudo/anuncios', formData);
                toast.success('Anúncio criado com sucesso!');
            }
            closeModal();
            setRefreshKey(k => k + 1);
        } catch (error) {
            toast.error(
                error.response?.data?.error ||
                error.response?.data?.message ||
                'Falha ao salvar anúncio.'
            );
        }
    }, [closeModal]);

    const TABS = [
        { key: 'slideshow', label: 'Banners',   icon: <FaImages />   },
        { key: 'anuncios',  label: 'Anúncios',  icon: <FaBullhorn /> },
        { key: 'logs',      label: 'Histórico', icon: <FaHistory />  },
    ];

    return (
        <div className="gc-main-container">
            <div className="gc-content-card">
                <div className="gc-tabs-container">
                    {TABS.map(t => (
                        <button
                            key={t.key}
                            className={`gc-tab-btn ${tab === t.key ? 'active' : ''}`}
                            onClick={() => setTab(t.key)}
                        >
                            {t.icon} {t.label}
                        </button>
                    ))}
                </div>

                <div className="gc-tab-panel">
                    {tab === 'slideshow' && <SlideshowManager isAdminGeral={isAdminGeral} />}
                    {tab === 'anuncios'  && <AnunciosManager onOpenModal={openModal} refreshKey={refreshKey} isAdminGeral={isAdminGeral} />}
                    {tab === 'logs'      && <LogsManager />}
                </div>
            </div>

            {modalOpen && (
                <AnuncioFormModal
                    anuncio={editing}
                    targetTenantId={anuncioTargetTenant}
                    onClose={closeModal}
                    onSave={saveAnuncio}
                    isAdminGeral={isAdminGeral}
                />
            )}
        </div>
    );
}